/**
 * Ando Connect — world-facing Space API helpers (June 2026).
 *
 * BE surface in ``ando-agent/src/api/v1/endpoints/connect.py``. Three auth tiers:
 *
 *   OWNER + MEMBER (protected ``/connect``, JWT via httpOnly cookie OR guest):
 *     POST   /connect/spaces                              createSpace
 *     GET    /connect/spaces                              listSpaces
 *     GET    /connect/spaces/{id}                         getSpace          (owner OR member)
 *     PATCH  /connect/spaces/{id}                         patchSpace        (rename/archive/mode/caps)
 *     POST   /connect/spaces/{id}/grants                  addGrant
 *     DELETE /connect/spaces/{id}/grants/{grant_id}       revokeGrant
 *     POST   /connect/spaces/{id}/members                 inviteMember
 *     DELETE /connect/spaces/{id}/members/{member_id}     revokeMember
 *     POST   /connect/spaces/{id}/chatbot/enable          enableChatbot
 *     POST   /connect/spaces/{id}/chatbot/disable         disableChatbot
 *     POST   /connect/spaces/{id}/ask                     askInSpace        (member-gated)
 *
 *   PUBLIC (unauthenticated ``/public/connect``, per-IP rate-limited):
 *     GET    /public/connect/invite/{token}               getInvitePreview
 *     POST   /public/connect/invite/{token}/accept        acceptInvite      → guest credential
 *     GET    /public/connect/chatbot/{public_token}       getChatbotBootstrap
 *     POST   /public/connect/chatbot/{public_token}/ask   askChatbot        (anonymous)
 *
 * Three caller identities, three transport rules:
 *   - authed Ando user → cookie (``credentials: 'include'``) via ``apiClient``.
 *   - magic-link guest → ``Authorization: ConnectGuest <token>`` header. The
 *     member ``/ask`` endpoint accepts EITHER; we attach the guest header when
 *     a credential is supplied (caller-set Authorization wins in ApiClient) and
 *     fall back to the cookie path otherwise — both DRY through ``apiClient``.
 *   - anonymous visitor → public endpoints, NO cookies / NO refresh pipeline
 *     (mirror of ``shares.getPublicShare``).
 */

import { getEnv } from "../../env.js";
import { apiClient } from "../ApiClient.js";

// ---------------------------------------------------------------------------
// Wire types — keep snake_case (BE-canonical).
// ---------------------------------------------------------------------------

export type ConnectSpaceMode = "collaboration" | "chatbot";
export type ConnectSpaceStatus = string;
export type ConnectGrantType = "knowledge" | "connector" | "document";
export type ConnectMemberRole = "viewer" | "editor";
export type ConnectMemberStatus = string;
export type ConnectInvitePrincipalType = "guest_email" | "internal" | "org_user";

/** Summary row returned by ``listSpaces`` / detail core (extra fields allowed). */
export interface ConnectSpace {
  id: string;
  name: string;
  description: string | null;
  status: ConnectSpaceStatus;
  mode: ConnectSpaceMode;
  public_token: string | null;
  daily_query_cap?: number | null;
  monthly_query_cap?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  member_count?: number;
  grant_count?: number;
  [key: string]: unknown;
}

export interface ConnectMember {
  id: string;
  role: ConnectMemberRole | string;
  email: string | null;
  status: ConnectMemberStatus;
  principal_type?: ConnectInvitePrincipalType | string;
  user_id?: string | null;
  invite_token?: string | null;
  expires_at?: string | null;
  created_at?: string | null;
  [key: string]: unknown;
}

export interface ConnectGrant {
  id: string;
  grant_type: ConnectGrantType;
  /** knowledge */
  canvas_id?: string | null;
  content_item_ids?: string[] | null;
  /** connector (connection-based, read-only) */
  connection_id?: string | null;
  table_allowlist?: string[] | null;
  /** document */
  content_item_id?: string | null;
  config?: Record<string, unknown> | null;
  created_at?: string | null;
  [key: string]: unknown;
}

/** Detail payload — owner console + member view (``getSpace`` / ``patchSpace``). */
export interface ConnectSpaceDetail extends ConnectSpace {
  members?: ConnectMember[];
  grants?: ConnectGrant[];
  /**
   * Viewer's relationship to THIS Space, decided server-side from the caller's
   * identity (June 2026, cross-org phase). ``owner`` → the full management
   * console; ``member`` → a cross-org member who was invited into a Space
   * owned by ANOTHER org and must see the read-only member view, never the
   * owner controls. The FE gates the management UI on this (defense-in-depth:
   * the BE 403s the management endpoints for non-owners regardless).
   */
  viewer_role?: ConnectMemberRole | "owner" | "member" | string;
  /** Convenience boolean mirror of ``viewer_role === 'owner'`` (BE-set). */
  is_owner?: boolean;
}

/**
 * One "shared with me" row (``GET /connect/spaces/shared``).
 *
 * Lightweight summary of a Space owned by ANOTHER org where the caller is an
 * ACTIVE member — NOT the owner's full grant/member graph. The counterpart is
 * the owning org (``owner_org_name``); the caller's own membership is carried
 * as ``member_id`` / ``member_role``. ``last_activity_at`` is the Space's
 * ``updated_at`` (a cheap recency signal). Extra keys are tolerated so the
 * same ``<SpaceCard>`` can read a Space-like shape, but the BE projection is
 * deliberately narrow (no caps / no owner access-log).
 */
export interface ConnectSharedSpace {
  id: string;
  name: string | null;
  description: string | null;
  mode: ConnectSpaceMode;
  status: ConnectSpaceStatus;
  /** Display name of the owning organization (the counterpart). */
  owner_org_name: string | null;
  /** The caller's own membership row id in this Space. */
  member_id: string | null;
  /** The caller's role within this Space. */
  member_role: ConnectMemberRole | string | null;
  /** Space ``updated_at`` surfaced as a recency stamp. */
  last_activity_at: string | null;
  [key: string]: unknown;
}

/**
 * One free-form, human-to-human message in a Space's member discussion thread
 * (``GET/POST /connect/spaces/{id}/messages``).
 *
 * This is the DISTINCT counterpart to the scoped AI Q&A (``askInSpace`` /
 * ``ConnectChat``): a plain conversation between the two collaborating
 * companies, NOT an LLM turn. ``author_label`` is a server-rendered display
 * name (the BE owns identity resolution for both JWT members and magic-link
 * guests); ``author_member_id`` is the membership row id when the author is a
 * member, ``null`` for the owner / system. ``is_own`` is decided server-side
 * from the CALLER's identity so the FE can right-align the viewer's own
 * messages without re-deriving identity client-side.
 */
export interface ConnectMessage {
  id: string;
  space_id: string;
  author_label: string;
  author_member_id: string | null;
  body: string;
  created_at: string;
  is_own: boolean;
  [key: string]: unknown;
}

export interface ConnectCitation {
  [key: string]: unknown;
}

/** Response of the scoped Q&A endpoints (``askInSpace`` / ``askChatbot``). */
export interface ConnectAskResponse {
  answer_markdown: string;
  citations: ConnectCitation[];
  status: string;
  capped: boolean;
  allowed_doc_count: number;
  conversation_id?: string | null;
  [key: string]: unknown;
}

export interface ConnectInvitePreview {
  space: {
    id: string;
    name: string | null;
  };
  role: ConnectMemberRole | string | null;
  email: string | null;
  status: ConnectMemberStatus | null;
  expires_at: string | null;
}

/** Result of ``acceptInvite`` — opaque ConnectGuest credential + space binding. */
export interface ConnectAcceptInviteResponse {
  credential: string;
  space_id: string;
  member: {
    id: string;
    role: ConnectMemberRole | string | null;
    email: string | null;
    status: ConnectMemberStatus | null;
  };
}

/** Embed snippet/URL returned by ``enableChatbot`` (``chatbot_embed``). */
export interface ConnectChatbotEnableResponse {
  space: ConnectSpace;
  /** ``chatbot_embed`` spreads its keys (embed URL / snippet) alongside ``space``. */
  [key: string]: unknown;
}

export interface ConnectChatbotDisableResponse {
  space: ConnectSpace;
}

export interface ConnectChatbotBootstrap {
  space_id: string;
  name: string | null;
  description: string | null;
  /** Display name of the owning organization (branded chatbot header). */
  owner_org_name?: string | null;
  /** Normalized read-only scope summary — same shape as ``getSpaceScope``. */
  scope?: ConnectScopeSummary | null;
}

/**
 * One normalized scope resource — the wire shape returned by
 * ``GET /connect/spaces/{id}/scope`` and embedded in the chatbot bootstrap.
 *
 * ``label`` is a server-rendered, human-readable description (already
 * localized / pluralized BE-side). ``count`` is an optional multiplicity hint.
 * The FE banner pairs each row's ``type`` with an icon and renders ``label`` —
 * scope is NEVER conveyed by colour alone.
 */
export interface ConnectScopeResource {
  type: ConnectGrantType;
  label: string;
  count?: number | null;
}

/**
 * Read-only scope summary for a Space, resolvable by BOTH a JWT member and a
 * magic-link guest (``ConnectGuest`` credential). It replaces the old
 * "doc-count only" placeholder: the guest/chatbot surfaces now render the SAME
 * full chip list the owner sees.
 */
export interface ConnectScopeSummary {
  owner_org_name: string | null;
  read_only: boolean;
  resources: ConnectScopeResource[];
}

/**
 * One grantable structured-data connection (``GET /connect/grantable-connections``).
 *
 * A connector grant is now CONNECTION-BASED: the owner picks a connected
 * structured-data source (a spreadsheet / database connection) and the BE
 * exposes it read-only. ``connection_id`` is the exact id the BE accepts on a
 * connector grant; ``label`` is the white-label display name (never a vendor
 * brand); ``provider`` feeds the picker glyph/theme. The BE owns the list of
 * grantable connections (fail-closed: only structured-data, read-only-safe
 * sources are returned) so the offered options can never drift from what the
 * BE accepts.
 */
export interface ConnectGrantableConnection {
  connection_id: string;
  label: string;
  provider: string;
}

/**
 * One owner-side access-log entry (``GET /connect/spaces/{id}/activity``).
 *
 * Audit fields ONLY — the underlying ``connect_access_log`` table has no
 * cost/EUR columns, so none appear here (and none must ever be surfaced on a
 * customer-facing read of this feed). Extra fields are tolerated for
 * forward-compat with a still-evolving BE projection.
 */
export interface ConnectActivityEntry {
  actor_label: string | null;
  success: boolean;
  query_preview: string | null;
  row_count: number | null;
  error: string | null;
  created_at: string | null;
  [key: string]: unknown;
}

// --- Request bodies -------------------------------------------------------

export interface CreateConnectSpaceRequest {
  name: string;
  description?: string | null;
}

export interface UpdateConnectSpaceRequest {
  name?: string | null;
  description?: string | null;
  archive?: boolean;
  mode?: ConnectSpaceMode;
  daily_query_cap?: number | null;
  monthly_query_cap?: number | null;
  clear_daily_cap?: boolean;
  clear_monthly_cap?: boolean;
}

export interface ConnectGrantRequest {
  grant_type: ConnectGrantType;
  /** knowledge */
  canvas_id?: string | null;
  content_item_ids?: string[] | null;
  /**
   * connector — CONNECTION-BASED. The owner picks a structured-data connection
   * (``connection_id``); an optional table allowlist rides in
   * ``config.table_allowlist`` (empty/omitted = the whole connection,
   * read-only). The BE forces ``read_only`` server-side.
   */
  connection_id?: string | null;
  /** document */
  content_item_id?: string | null;
  config?: Record<string, unknown> | null;
}

export interface ConnectInviteRequest {
  principal_type: ConnectInvitePrincipalType;
  role?: ConnectMemberRole;
  /** required when principal_type === 'guest_email' */
  email?: string;
  /** required when principal_type === 'internal' | 'org_user' */
  user_id?: string;
}

export interface ConnectAskRequest {
  question: string;
  conversation_id?: string | null;
}

// ---------------------------------------------------------------------------
// Guest credential helper.
//
// The magic-link guest replays the opaque credential from ``acceptInvite`` via
// ``Authorization: ConnectGuest <token>``. ``ApiClient.request`` honours a
// caller-set Authorization header (it only auto-injects ``Bearer`` when none is
// present), so this single helper lets ``askInSpace`` support BOTH the
// authed-cookie path (omit the credential) and the guest path (supply it) DRY.
// ---------------------------------------------------------------------------

function guestAuthHeader(guestCredential?: string | null): HeadersInit | undefined {
  if (!guestCredential) return undefined;
  return { Authorization: `ConnectGuest ${guestCredential}` };
}

// ---------------------------------------------------------------------------
// Authenticated endpoints (JWT via httpOnly cookie, or guest header for /ask).
// ---------------------------------------------------------------------------

const BASE = "/connect";

export async function createSpace(body: CreateConnectSpaceRequest): Promise<ConnectSpace> {
  return apiClient.post<ConnectSpace>(`${BASE}/spaces`, body);
}

export async function listSpaces(): Promise<ConnectSpace[]> {
  return apiClient.get<ConnectSpace[]>(`${BASE}/spaces`);
}

/**
 * Spaces shared WITH the caller by OTHER orgs (``GET /connect/spaces/shared``).
 *
 * Mirrors ``listSpaces`` (cookie/JWT, ``apiClient``) but returns the narrow
 * member-side projection (``ConnectSharedSpace``): the caller is an ACTIVE
 * member, never the owner. The literal ``shared`` segment is declared BEFORE
 * ``/spaces/{space_id}`` on the BE so it is not captured as a path param.
 */
export async function listSharedSpaces(): Promise<ConnectSharedSpace[]> {
  return apiClient.get<ConnectSharedSpace[]>(`${BASE}/spaces/shared`);
}

export async function getSpace(spaceId: string): Promise<ConnectSpaceDetail> {
  return apiClient.get<ConnectSpaceDetail>(`${BASE}/spaces/${encodeURIComponent(spaceId)}`);
}

/**
 * Read-only scope summary for a Space (``GET /connect/spaces/{id}/scope``).
 *
 * Resolvable by BOTH identities — exactly like ``askInSpace``:
 *   - authed Ando member → omit ``guestCredential`` (cookie path).
 *   - magic-link guest   → pass the credential from ``acceptInvite`` →
 *     ``Authorization: ConnectGuest <token>`` (caller-set Authorization wins).
 *
 * Returns the owning org name + the full normalized ``resources`` list so the
 * guest surface can render the SAME chip banner the owner sees.
 */
export async function getSpaceScope(
  spaceId: string,
  guestCredential?: string | null,
): Promise<ConnectScopeSummary> {
  const headers = guestAuthHeader(guestCredential);
  return apiClient.get<ConnectScopeSummary>(
    `${BASE}/spaces/${encodeURIComponent(spaceId)}/scope`,
    headers ? { headers } : undefined,
  );
}

/**
 * The structured-data connections an owner may grant into a Space, read-only
 * (``GET /connect/grantable-connections``).
 *
 * Connector grants are CONNECTION-BASED: the owner picks one of the connections
 * the BE returns here and the source is exposed read-only (optionally scoped to
 * a table allowlist). The BE owns the list (fail-closed: only structured-data,
 * read-only-safe connections appear), so the grant form renders its picker from
 * THIS list — ``connection_id`` is the exact id the BE accepts on submit.
 */
export async function listGrantableConnections(): Promise<ConnectGrantableConnection[]> {
  const res = await apiClient.get<{ connections?: ConnectGrantableConnection[] }>(
    `${BASE}/grantable-connections`,
  );
  return res.connections ?? [];
}

export async function patchSpace(
  spaceId: string,
  body: UpdateConnectSpaceRequest,
): Promise<ConnectSpaceDetail> {
  return apiClient.patch<ConnectSpaceDetail>(`${BASE}/spaces/${encodeURIComponent(spaceId)}`, body);
}

export async function addGrant(spaceId: string, body: ConnectGrantRequest): Promise<ConnectGrant> {
  return apiClient.post<ConnectGrant>(`${BASE}/spaces/${encodeURIComponent(spaceId)}/grants`, body);
}

export async function revokeGrant(spaceId: string, grantId: string): Promise<void> {
  await apiClient.delete<void>(
    `${BASE}/spaces/${encodeURIComponent(spaceId)}/grants/${encodeURIComponent(grantId)}`,
  );
}

export async function inviteMember(
  spaceId: string,
  body: ConnectInviteRequest,
): Promise<ConnectMember> {
  return apiClient.post<ConnectMember>(
    `${BASE}/spaces/${encodeURIComponent(spaceId)}/members`,
    body,
  );
}

export async function revokeMember(spaceId: string, memberId: string): Promise<void> {
  await apiClient.delete<void>(
    `${BASE}/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(memberId)}`,
  );
}

export async function enableChatbot(spaceId: string): Promise<ConnectChatbotEnableResponse> {
  return apiClient.post<ConnectChatbotEnableResponse>(
    `${BASE}/spaces/${encodeURIComponent(spaceId)}/chatbot/enable`,
  );
}

export async function disableChatbot(spaceId: string): Promise<ConnectChatbotDisableResponse> {
  return apiClient.post<ConnectChatbotDisableResponse>(
    `${BASE}/spaces/${encodeURIComponent(spaceId)}/chatbot/disable`,
  );
}

/**
 * Owner-side activity feed (``GET /connect/spaces/{id}/activity``).
 *
 * Returns the recent access-log entries (audit fields only — no cost/EUR). A
 * real error (403 non-owner, 5xx) surfaces to the caller's error state.
 */
export async function getSpaceActivity(
  spaceId: string,
  limit = 50,
): Promise<readonly ConnectActivityEntry[]> {
  return apiClient.get<readonly ConnectActivityEntry[]>(
    `${BASE}/spaces/${encodeURIComponent(spaceId)}/activity?limit=${encodeURIComponent(limit)}`,
  );
}

/**
 * Ask a scoped question against a Space (member-gated).
 *
 * Supports BOTH callers:
 *   - authed Ando user → omit ``guestCredential`` (cookie path).
 *   - magic-link guest → pass the credential from ``acceptInvite`` →
 *     ``Authorization: ConnectGuest <token>`` (caller-set Authorization wins).
 */
export async function askInSpace(
  spaceId: string,
  body: ConnectAskRequest,
  guestCredential?: string | null,
): Promise<ConnectAskResponse> {
  const headers = guestAuthHeader(guestCredential);
  return apiClient.post<ConnectAskResponse>(
    `${BASE}/spaces/${encodeURIComponent(spaceId)}/ask`,
    body,
    headers ? { headers } : undefined,
  );
}

// ---------------------------------------------------------------------------
// Member-to-member discussion thread (free-form human messages).
//
// DISTINCT from the scoped AI Q&A (``askInSpace``): a plain conversation
// between the two collaborating companies. Both endpoints sit behind the SAME
// ``require_space_access`` gate as ``askInSpace`` / ``getSpaceScope`` and so
// accept BOTH identities — omit ``guestCredential`` for the authed-cookie path,
// pass it for the magic-link guest (``Authorization: ConnectGuest <token>``,
// caller-set Authorization wins in ApiClient). DRY through the SAME
// ``guestAuthHeader`` helper.
// ---------------------------------------------------------------------------

/** List the discussion-thread messages for a Space (newest-last, paginated). */
export async function listSpaceMessages(
  spaceId: string,
  params?: { limit?: number; before?: string },
  guestCredential?: string | null,
): Promise<ConnectMessage[]> {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.before) search.set("before", params.before);
  const qs = search.toString();
  const headers = guestAuthHeader(guestCredential);
  const res = await apiClient.get<{ messages?: ConnectMessage[] }>(
    `${BASE}/spaces/${encodeURIComponent(spaceId)}/messages${qs ? `?${qs}` : ""}`,
    headers ? { headers } : undefined,
  );
  return res.messages ?? [];
}

/** Post a free-form message to a Space's discussion thread. */
export async function postSpaceMessage(
  spaceId: string,
  body: string,
  guestCredential?: string | null,
): Promise<ConnectMessage> {
  const headers = guestAuthHeader(guestCredential);
  return apiClient.post<ConnectMessage>(
    `${BASE}/spaces/${encodeURIComponent(spaceId)}/messages`,
    { body },
    headers ? { headers } : undefined,
  );
}

// ---------------------------------------------------------------------------
// Public endpoints — no auth, no cookies.
//
// Anonymous visitors have no ``ando_session`` cookie. We deliberately skip
// ``credentials: 'include'`` and the 401-refresh / device-fingerprint pipeline
// so the request is exactly what the BE's ``public_router`` expects (mirror of
// ``shares.getPublicShare``). Status mapping (BE ``_map_value_error``):
//   404           → notfound
//   410 "expired" → expired
//   410 "revoked" / "already_accepted" → gone
//   other         → error
// ---------------------------------------------------------------------------

export type ConnectPublicErrorReason = "notfound" | "expired" | "gone" | "rate_limited" | "error";

export class ConnectPublicError extends Error {
  readonly status: number;
  readonly reason: ConnectPublicErrorReason;
  readonly detail: string;

  constructor(message: string, status: number, reason: ConnectPublicErrorReason, detail = "") {
    super(message);
    this.name = "ConnectPublicError";
    this.status = status;
    this.reason = reason;
    this.detail = detail;
  }
}

interface PublicFetchOptions {
  method: "GET" | "POST";
  body?: unknown;
}

async function publicConnectFetch<T>(path: string, options: PublicFetchOptions): Promise<T> {
  const base = getEnv().apiUrl;
  const url = `${base}${path}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: options.method,
      headers:
        options.body !== undefined
          ? { Accept: "application/json", "Content-Type": "application/json" }
          : { Accept: "application/json" },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      // No credentials — public by design.
    });
  } catch (err) {
    throw new ConnectPublicError(err instanceof Error ? err.message : "Network error", 0, "error");
  }

  if (res.ok) {
    return (await res.json()) as T;
  }

  let detail = "";
  try {
    const data = (await res.json()) as { detail?: string };
    detail = data?.detail ?? "";
  } catch {
    // not JSON — keep detail empty
  }

  if (res.status === 404) {
    throw new ConnectPublicError(detail || "Not found", 404, "notfound", detail);
  }
  if (res.status === 410) {
    const reason: ConnectPublicErrorReason = detail.toLowerCase().includes("expire")
      ? "expired"
      : "gone";
    throw new ConnectPublicError(detail || "No longer available", 410, reason, detail);
  }
  if (res.status === 429) {
    throw new ConnectPublicError(detail || "Too many requests", 429, "rate_limited", detail);
  }
  throw new ConnectPublicError(
    detail || `Request failed with status ${res.status}`,
    res.status,
    "error",
    detail,
  );
}

export async function getInvitePreview(token: string): Promise<ConnectInvitePreview> {
  return publicConnectFetch<ConnectInvitePreview>(
    `/public/connect/invite/${encodeURIComponent(token)}`,
    { method: "GET" },
  );
}

export async function acceptInvite(token: string): Promise<ConnectAcceptInviteResponse> {
  return publicConnectFetch<ConnectAcceptInviteResponse>(
    `/public/connect/invite/${encodeURIComponent(token)}/accept`,
    { method: "POST", body: {} },
  );
}

export async function getChatbotBootstrap(publicToken: string): Promise<ConnectChatbotBootstrap> {
  return publicConnectFetch<ConnectChatbotBootstrap>(
    `/public/connect/chatbot/${encodeURIComponent(publicToken)}`,
    { method: "GET" },
  );
}

export async function askChatbot(
  publicToken: string,
  body: ConnectAskRequest,
): Promise<ConnectAskResponse> {
  return publicConnectFetch<ConnectAskResponse>(
    `/public/connect/chatbot/${encodeURIComponent(publicToken)}/ask`,
    { method: "POST", body },
  );
}
